import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Typography } from "../../../design-system";
import { colors } from "../../../design-system/colors";
import logoSite from "../../../../assets/brand/logo_site.svg";

interface SkeletonMediaRowProps {
  statusText: string;
  columnWidths: string[];
}

// Friendly stage labels in pipeline order (upload → transcribe → find moments → cut).
// They reflect the real stages so the user sees motion and knows it's working — not a
// frozen "Pending". They rotate so it never looks stuck.
const PROCESS_STEPS = [
  "Processing your video…",
  "Transcribing the audio…",
  "Finding every quote & moment…",
  "Cutting your clips…",
  "Almost ready…",
];

// Live "it's working" status: pulsing cherry logo inside a spinner + a rotating step
// label, plus an honest expectation line so users know roughly how long to wait.
const ProcessingStatus: React.FC = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % PROCESS_STEPS.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: "3px", maxWidth: "280px" }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
        <Box sx={{ position: "relative", width: 24, height: 24, flexShrink: 0 }}>
          <CircularProgress size={24} sx={{ color: colors.primary[500], position: "absolute", inset: 0 }} />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 12,
              height: 12,
              backgroundImage: `url(${logoSite})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              animation: "cherryPulse 1.4s ease-in-out infinite",
              "@keyframes cherryPulse": {
                "0%, 100%": { opacity: 0.55, transform: "translate(-50%, -50%) scale(0.85)" },
                "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.1)" },
              },
            }}
          />
        </Box>
        <Typography variant="button" color="white" sx={{ whiteSpace: "nowrap", lineHeight: "20px" }}>
          {PROCESS_STEPS[step]}
        </Typography>
      </Box>

      {/* Progress bar — grows as the stages complete, then eases to a near-stop just
          before the final step (it's an estimate, not exact). When processing finishes,
          the row is replaced by the real clip, so the bar never has to "complete". */}
      <Box sx={{ width: "100%", height: "5px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden", mt: "2px" }}>
        <Box
          sx={{
            height: "100%",
            borderRadius: "3px",
            background: "#e61d50",
            width: "4%",
            animation: "procGrow 90s cubic-bezier(0.05, 0.7, 0.1, 1) forwards",
            "@keyframes procGrow": {
              "0%": { width: "4%" },
              "100%": { width: "92%" }, // eases up to ~92% and holds there until done
            },
          }}
        />
      </Box>

      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.45)", textAlign: "right", lineHeight: 1.35, fontFamily: "Outfit, sans-serif" }}>
        Short clips ~1 min · long videos 5–20 min. Best to wait until it's done.
      </Typography>
    </Box>
  );
};

const shimmerSx = {
  backgroundSize: "300% 100%",
  animation: "shimmer 3s linear infinite",
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "150% center" },
    "100%": { backgroundPosition: "-150% center" },
  },
} as const;

const Pill: React.FC<{
  width: number;
  height: number;
  gradient: string;
}> = ({ width, height, gradient }) => (
  <Box
    sx={{
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: "48px",
      background: gradient,
      opacity: 0.8,
      flexShrink: 0,
      ...shimmerSx,
    }}
  />
);

const SkeletonThumbnailCell: React.FC<{ width: string }> = ({ width }) => (
  <td style={{ width, verticalAlign: "middle" }}>
    <Box
      sx={{
        width: "139px",
        height: "85px",
        borderRadius: "8px",
        background: colors.skeleton.thumbnailGradient,
        flexShrink: 0,
        ...shimmerSx,
      }}
    />
  </td>
);

const SkeletonNameCell: React.FC<{ width: string }> = ({ width }) => (
  <td style={{ width, verticalAlign: "middle" }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        paddingLeft: "24px",
      }}
    >
      <Pill width={247} height={16} gradient={colors.skeleton.pillGradient} />
      <Pill width={300} height={14} gradient={colors.skeleton.pillGradient} />
    </Box>
  </td>
);

const SkeletonSimpleCell: React.FC<{ width: string }> = ({ width }) => (
  <td style={{ width, verticalAlign: "middle" }}>
    <Box sx={{ paddingLeft: "24px" }}>
      <Pill width={72} height={16} gradient={colors.skeleton.pillGradient} />
    </Box>
  </td>
);

const SkeletonStatusCell: React.FC = () => (
  <td style={{ verticalAlign: "middle", textAlign: "right" }}>
    <ProcessingStatus />
  </td>
);

const SkeletonMediaRow: React.FC<SkeletonMediaRowProps> = ({
  columnWidths,
}) => (
  <>
    <SkeletonThumbnailCell width={columnWidths[0]} />
    <SkeletonNameCell width={columnWidths[1]} />
    <SkeletonSimpleCell width={columnWidths[2]} />
    <SkeletonSimpleCell width={columnWidths[3]} />
    <SkeletonSimpleCell width={columnWidths[4]} />
    <SkeletonStatusCell />
  </>
);

export default SkeletonMediaRow;
